<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid">
        <div class="row">
          <div class="col-xl-12">
            <div class="chart-info">
              <div class="chart-info__left">
                <h3 class="title-1 m-b-15">
                  <span><i class="zmdi zmdi-calendar-note p-r-10"></i>{{ text('overview') }}</span>
                  <date-range-picker
                    :locale-data="pickerLocale"
                    :opens="'right'"
                    :ranges="pickerRanges"
                    v-model="dateRange"
                    :autoApply="true"
                    :timePicker="true"
                    @update="onDateChange">
                  </date-range-picker>
                </h3>
              </div>
            </div>
            <div class="row m-t-25">
              <div class="col-sm-6 col-lg-3">
                <div class="statistic__item">
                  <h2 class="number">
                    <span class="spinner-border spinner-grow-sm"
                          v-if="$store.getters.isDataLoaded">
                    </span>
                    <span v-if="!$store.getters.isDataLoaded">
                      {{ formatBytes($store.getters.totalTransferred) }}
                    </span>
                  </h2>
                  <span class="desc">{{ text('traffic_total') }}</span>
                  <div class="icon">
                    <i class="zmdi zmdi-calendar-note"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-6 col-lg-9">
                <div class="cached-progress">
                  <h3 class="title-3">{{ text('cached_non_cached') }}</h3>
                  <div class="au-skill-container">
                    <div class="au-progress">
                      <div class="au-progress__bar">
                        <b-progress :max="100" class="mb-2" variant="success">
                          <b-progress-bar :label="`${cached}%`" :value="cached"></b-progress-bar>
                        </b-progress>
                      </div>
                    </div>
                    <div class="au-progress">
                      <div class="au-progress__bar">
                        <b-progress :max="100" class="mb-2" variant="danger">
                          <b-progress-bar :label="`${nonCached}%`" :value="nonCached">
                          </b-progress-bar>
                        </b-progress>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="usage-data m-b-40">
              <div class="table-responsive table-data">
                <div class="el-loading-mask" v-if="loading">
                  <div class="el-loading-spinner">
                    <svg viewBox="25 25 50 50" class="circular">
                      <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                    </svg>
                  </div>
                </div>
                <table class="table">
                  <thead>
                  <tr>
                    <td>{{ text('endpoint') }}</td>
                    <td>{{ text('traffic') }}</td>
                    <td>{{ text('cached') }}</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr :key="i" v-for="(endpoint, i) of endpoints"
                      @click="showEndpointInfo(endpoint.id)">
                    <td>
                      <div class="table-data__info">
                        <h6>{{ endpoint.name }}</h6>
                        <span>
                          <a :href="endpoint.url" target="_blank">
                            {{ endpoint.url }}
                          </a>
                        </span>
                      </div>
                    </td>
                    <td>
                      <span>
                        {{ formatBytes($store.getters.resourceTransferredBytes(endpoint.id), 2) }}
                      </span>
                    </td>
                    <td>
                      {{ formatBytes($store.getters.resourceCachedBytes(endpoint.id), 2) }}
                    </td>
                  </tr>
                  <tr v-if="!loading && !endpoints.length">
                    <td class="text-center text-muted" colspan="3">
                      {{ text('no_traffic') }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import moment from 'moment';
import { JsonAPIEndpointToNorm } from '@/components/cdn/shared/converter';

export default {
  components: {
    DateRangePicker,
  },
  data() {
    return {
      endpoints: [],
      dateRange: {
        startDate: moment().subtract(1, 'week'),
        endDate: moment(),
      },
      pickerLocale: {
        direction: 'ltr',
        format: 'DD MMMM YYYY',
        separator: ' - ',
        applyLabel: 'Apply',
        cancelLabel: 'Cancel',
        weekLabel: 'W',
        customRangeLabel: 'Custom Range',
        daysOfWeek: moment.weekdaysMin(),
        monthNames: moment.monthsShort(),
        firstDay: 1,
        showWeekNumbers: true,
      },
      pickerRanges: {
        'Last 24 Hours': [moment().subtract(1, 'days'), moment()],
        'Last 7 Days': [moment().subtract(1, 'week'), moment()],
        'Last 30 Days': [moment().subtract(1, 'month'), moment()],
        'This month': [moment().startOf('month'), moment().endOf('month')],
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    cached() {
      if (this.$store.getters.totalCached > 0 && this.$store.getters.totalTransferred > 0) {
        // eslint-disable-next-line max-len
        return Math.round(((this.$store.getters.totalCached * 100) / this.$store.getters.totalTransferred) * 100) / 100;
      }
      return 0;
    },
    nonCached() {
      return Math.round((100 - this.cached) * 100) / 100;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  created() {
    this.loadData(this.dateRange.startDate, this.dateRange.endDate);
  },
  methods: {
    onDateChange(value) {
      const start = moment(value.startDate);
      const end = moment(value.endDate);
      this.loadData(start, end);
    },
    showEndpointInfo(id) {
      this.$router.push({
        name: 'EndpointDetailPage',
        params: {
          id,
        },
      });
    },
    loadData(start, end) {
      this.$store.commit('setPageLoader');
      this.$store.dispatch('endpoints/getCDNEndpoints')
        .then((endpoints) => {
          this.endpoints = endpoints.map(e => JsonAPIEndpointToNorm(e));
          const query = {
            start,
            end,
          };
          this.$store.dispatch('getUsage', query)
            .catch((errorMsg) => {
              this.$toasted.error(errorMsg, {
                theme: 'toasted-primary',
                position: 'top-center',
                duration: 10000,
              });
            });
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
        })
        .finally(() => {
          this.$store.commit('resolvePageLoader');
        });
    },
    formatBytes(bytes, decimals = 0) {
      if (bytes === 0) return '0 KB';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
